import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';
import { getApiUrl } from '../utils/api';

const stringToColor = (str) => {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash) + str.charCodeAt(i);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

const RepoStructure = ({ searchRepoStructureLoading, repoStructure, currentPath, navigateToFolder, goBack, subtitle, logo, muteClusterAlerts }) => {
    const [reservations, setReservations] = useState({});
    const [loadingReservations, setLoadingReservations] = useState({});
    const [favorites, setFavorites] = useState({});
    const [loadingFavorites, setLoadingFavorites] = useState({});
    const [appTypeFilter, setAppTypeFilter] = useState('octo'); // Default to Octo apps

    // Check if current path is internal typology
    const isInternalTypology = () => {
        if (!currentPath) return false;
        const pathParts = currentPath.split('/').filter(Boolean);
        return pathParts[0] === 'internal';
    };

    // Check if item is an app in internal typology
    const isInternalApp = (itemPath) => {
        const pathParts = itemPath.split('/').filter(Boolean);
        return pathParts.length === 3 && pathParts[0] === 'internal';
    };

    // Check if an app is an Octo app based on metadata
    const isOctoApp = (item) => {
        // Check if the app has appType 'octo' in metadata
        return item.metadata && item.metadata.appType === 'octo';
    };

    // Fetch reservation status for an app
    const fetchReservationStatus = async (appPath, forceRefresh = false) => {
        // Prevent duplicate requests for the same app, unless force refresh is requested
        if (reservations[appPath] !== undefined && !forceRefresh) {
            return;
        }
        
        try {
            const response = await axios.get(getApiUrl(`/api/app-reservations/${encodeURIComponent(appPath)}`));
            setReservations(prev => ({
                ...prev,
                [appPath]: response.data
            }));
        } catch (error) {
            console.error('Error fetching reservation status:', error);
        }
    };

    // Reserve an app
    const reserveApp = async (appPath) => {
        setLoadingReservations(prev => ({ ...prev, [appPath]: true }));
        try {
            await axios.post(getApiUrl('/api/app-reservations/reserve'), {
                appPath,
                purpose: 'Testing'
            });
            toast.success('App reserved successfully!');
            
            // Fetch fresh reservation status
            await fetchReservationStatus(appPath, true);
        } catch (error) {
            if (error.response?.status === 409) {
                toast.error(`App is already reserved by ${error.response.data.reservation.reservedBy}`);
            } else {
                toast.error('Failed to reserve app');
            }
        } finally {
            setLoadingReservations(prev => ({ ...prev, [appPath]: false }));
        }
    };

    // Release an app
    const releaseApp = async (appPath) => {
        setLoadingReservations(prev => ({ ...prev, [appPath]: true }));
        try {
            await axios.post(getApiUrl('/api/app-reservations/release'), {
                appPath
            });
            toast.success('App reservation released!');
            
            // Fetch fresh reservation status
            await fetchReservationStatus(appPath, true);
        } catch (error) {
            toast.error('Failed to release app reservation');
        } finally {
            setLoadingReservations(prev => ({ ...prev, [appPath]: false }));
        }
    };

    // Fetch favorite status for an app
    const fetchFavoriteStatus = async (appPath, forceRefresh = false) => {
        if (favorites[appPath] !== undefined && !forceRefresh) {
            return;
        }
        
        try {
            const response = await axios.get(getApiUrl(`/api/favorites/check/${encodeURIComponent(appPath)}`));
            setFavorites(prev => ({
                ...prev,
                [appPath]: response.data
            }));
        } catch (error) {
            console.error('Error fetching favorite status:', error);
        }
    };

    // Add app to favorites
    const addToFavorites = async (appPath, appName, typology, cluster) => {
        setLoadingFavorites(prev => ({ ...prev, [appPath]: true }));
        try {
            await axios.post(getApiUrl('/api/favorites/add'), {
                appPath,
                appName,
                typology,
                cluster
            });
            toast.success('App added to favorites!');
            
            // Update favorite status
            setFavorites(prev => ({
                ...prev,
                [appPath]: { isFavorited: true }
            }));
        } catch (error) {
            console.error('Error adding to favorites:', error);
            toast.error('Failed to add to favorites');
        } finally {
            setLoadingFavorites(prev => ({ ...prev, [appPath]: false }));
        }
    };

    // Remove app from favorites
    const removeFromFavorites = async (appPath) => {
        setLoadingFavorites(prev => ({ ...prev, [appPath]: true }));
        try {
            await axios.delete(getApiUrl('/api/favorites/remove'), { data: { appPath } });
            toast.success('App removed from favorites!');
            
            // Update favorite status
            setFavorites(prev => ({
                ...prev,
                [appPath]: { isFavorited: false }
            }));
        } catch (error) {
            console.error('Error removing from favorites:', error);
            toast.error('Failed to remove from favorites');
        } finally {
            setLoadingFavorites(prev => ({ ...prev, [appPath]: false }));
        }
    };

    // Fetch reservation status for all internal apps
    useEffect(() => {
        if (isInternalTypology()) {
            const internalApps = repoStructure.filter(item => 
                item.type === 'commit_directory' && isInternalApp(item.path)
            );
            
            // Only fetch if we have internal apps and we're not already loading
            if (internalApps.length > 0) {
                const timeoutId = setTimeout(() => {
                    internalApps.forEach(app => {
                        fetchReservationStatus(app.path);
                    });
                }, 100); // Small delay to prevent rapid successive calls
                
                return () => clearTimeout(timeoutId);
            }
        } else {
            // Clear reservations when not in internal typology
            setReservations({});
        }
    }, [repoStructure, currentPath]); // Removed fetchReservationStatus from dependencies

    // Fetch favorite status for all apps
    useEffect(() => {
        const apps = repoStructure.filter(item => 
            item.type === 'commit_directory' && getCurrentStep() === 'app'
        );
        
        if (apps.length > 0) {
            const timeoutId = setTimeout(() => {
                apps.forEach(app => {
                    fetchFavoriteStatus(app.path);
                });
            }, 150); // Small delay to prevent rapid successive calls
            
            return () => clearTimeout(timeoutId);
        }
    }, [repoStructure, currentPath]);

    const getCurrentStep = () => {
        if (!currentPath) return 'typology';
        const pathParts = currentPath.split('/').filter(Boolean);
        if (pathParts.length === 1) return 'cluster';
        if (pathParts.length === 2) return 'app';
        return 'typology';
    };

    const getStepInfo = () => {
        const step = getCurrentStep();
        switch (step) {
            case 'typology':
                return {
                    title: 'Choose a Typology',
                    subtitle: 'Select your infrastructure environment',
                    icon: 'bi-layers',
                    color: 'primary',
                    description: 'Infrastructure typologies define your deployment environment'
                };
            case 'cluster':
                return {
                    title: `Choose a Cluster for ${currentPath.split('/')[0]}`,
                    subtitle: 'Select your Kubernetes cluster',
                    icon: 'bi-server',
                    color: 'success',
                    description: 'Kubernetes clusters manage your applications'
                };
            case 'app':
                return {
                    title: `Choose an App for ${currentPath.split('/')[1]}`,
                    subtitle: 'Select your application to deploy',
                    icon: 'bi-app',
                    color: 'warning',
                    description: 'Applications that can be deployed and managed'
                };
            default:
                return {
                    title: 'Navigation',
                    subtitle: 'Select an option',
                    icon: 'bi-folder',
                    color: 'secondary',
                    description: 'Navigate through your infrastructure'
                };
        }
    };

    const filterItemsByStep = () => {
        const step = getCurrentStep();
        const pathParts = currentPath ? currentPath.split('/').filter(Boolean) : [];
        
        let filtered = repoStructure.filter(item => {
            if (item.type !== 'commit_directory') return false;
            
            const itemPathParts = item.path.split('/').filter(Boolean);
            
            switch (step) {
                case 'typology':
                    return itemPathParts.length === 1;
                case 'cluster':
                    return itemPathParts.length === 2 && 
                           itemPathParts[0] === pathParts[0];
                case 'app':
                    return itemPathParts.length === 3 && 
                           itemPathParts[0] === pathParts[0] && 
                           itemPathParts[1] === pathParts[1];
                default:
                    return false;
            }
        });

        // Apply app type filter only at the app step
        if (step === 'app' && appTypeFilter === 'octo') {
            filtered = filtered.filter(item => isOctoApp(item));
        }

        return filtered;
    };

    const filteredItems = filterItemsByStep();
    const stepInfo = getStepInfo();
    const currentStep = getCurrentStep();

    return (
        <div className="smart-navigation">
            {/* Smart Header */}
            <motion.div 
                className="smart-header mb-4"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
            >
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                        {currentPath && (
                            <motion.button 
                                className="btn btn-outline-light "
                                onClick={goBack}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                title="Go Back"
                            >
                                <i className="bi bi-arrow-left"></i>
                            </motion.button>
                        )}
                        <div className="step-info">
                            <div className="d-flex align-items-center gap-3">
                                <div className={`step-icon-wrapper bg-${stepInfo.color} bg-opacity-10 p-3 rounded-circle`}>
                                    <i className={`bi ${stepInfo.icon} text-${stepInfo.color} fs-3`}></i>
                                </div>
                                <div>
                                    <h3 className="mb-1 fw-bold text-white">{stepInfo.title}</h3>
                                    <p className="mb-0 text-white-50">{stepInfo.subtitle}</p>
                                    <div className="progress mt-2" style={{height: '4px'}}>
                                        <div 
                                            className={`progress-bar bg-${stepInfo.color}`} 
                                            style={{width: `${currentStep === 'typology' ? '33%' : currentStep === 'cluster' ? '66%' : '100%'}`}}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="step-stats d-flex align-items-center gap-3">
                        {/* App Type Filter Toggle - Only show at app step */}
                        {currentStep === 'app' && (
                            <div className="btn-group" role="group">
                                <input 
                                    type="radio" 
                                    className="btn-check" 
                                    name="appTypeFilter" 
                                    id="octoAppsNav" 
                                    value="octo"
                                    checked={appTypeFilter === 'octo'}
                                    onChange={(e) => setAppTypeFilter(e.target.value)}
                                />
                                <label className="btn btn-outline-light btn-sm" htmlFor="octoAppsNav">
                                    <i className="bi bi-app me-1"></i>
                                    Octo Apps
                                </label>
                                
                                <input 
                                    type="radio" 
                                    className="btn-check" 
                                    name="appTypeFilter" 
                                    id="allAppsNav" 
                                    value="all"
                                    checked={appTypeFilter === 'all'}
                                    onChange={(e) => setAppTypeFilter(e.target.value)}
                                />
                                <label className="btn btn-outline-light btn-sm" htmlFor="allAppsNav">
                                    <i className="bi bi-collection me-1"></i>
                                    All Apps
                                </label>
                            </div>
                        )}
                        <span className={`badge bg-${stepInfo.color} bg-opacity-75`}>
                            {filteredItems.length} {currentStep === 'typology' ? 'typologies' : currentStep === 'cluster' ? 'clusters' : 'apps'}
                        </span>
                    </div>
                </div>
            </motion.div>

            {/* Loading State */}
            {searchRepoStructureLoading && (
                <motion.div 
                    className="loading-container text-center py-5"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                >
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted">Loading {currentStep}...</p>
                </motion.div>
            )}

            {/* Smart Items Grid */}
            {!searchRepoStructureLoading && (
                <motion.div 
                    className="smart-items-grid"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                >
                    <div className="row g-4">
                        {filteredItems.map((item, index) => {
                            const itemName = item.path.split('/').pop();
                            const isInternalAppItem = isInternalApp(item.path);
                            const reservation = reservations[item.path];
                            const favorite = favorites[item.path];
                            
                            return (
                                <motion.div 
                                    key={item.path}
                                    className="col-lg-4 col-md-6"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 * index }}
                                >
                                    <motion.div 
                                        className={`smart-item-card card h-100 border-0 shadow-sm ${
                                            reservation?.reserved ? 'reserved-app' : ''
                                        }`}
                                        whileHover={{ 
                                            scale: 1.02, 
                                            boxShadow: "0 8px 25px rgba(0,0,0,0.15)" 
                                        }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        <div className="card-body p-4 text-center">
                                            {/* Item Icon */}
                                            <div className="item-icon mb-3">
                                                <div className={`icon-wrapper bg-${stepInfo.color} bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center`} style={{width: '70px', height: '70px'}}>
                                                    <i className={`bi ${stepInfo.icon} text-${stepInfo.color} fs-1`}></i>
                                                </div>
                                            </div>
                                            
                                            {/* Item Name with App Type */}
                                            <h5 className="mb-2 fw-bold text-dark">
                                                {itemName}
                                                {item.metadata?.appType && (
                                                    <span 
                                                        className={`badge ms-2 ${
                                                            item.metadata.appType === 'octo' ? 'bg-primary' : 'bg-secondary'
                                                        }`}
                                                        style={{ fontSize: '0.6rem', verticalAlign: 'middle' }}
                                                    >
                                                        {item.metadata.appType === 'octo' ? 'Octo+' : 'Infra'}
                                                    </span>
                                                )}
                                            </h5>
                                            
                                            {/* Item Description */}
                                            <p className="text-muted mb-3 small">
                                                {stepInfo.description}
                                            </p>

                                            {/* Reservation Status for Internal Apps - More Prominent */}
                                            {isInternalAppItem && (
                                                <div className="reservation-status mb-3">
                                                    {reservation?.reserved ? (
                                                        <div className="reserved-badge">
                                                            <span className="badge bg-warning text-dark fw-semibold">
                                                                <i className="bi bi-lock-fill me-1"></i>
                                                                Reserved by {reservation.reservation.reservedBy}
                                                            </span>
                                                            <small className="d-block text-muted mt-1 fw-medium">
                                                                {new Date(reservation.reservation.reservedAt).toLocaleDateString()}
                                                            </small>
                                                        </div>
                                                    ) : (
                                                        <span className="badge bg-success fw-semibold">
                                                            <i className="bi bi-unlock-fill me-1"></i>
                                                            Available
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            {/* Version Info - Compact and Secondary */}
                                            {item.metadata && (item.metadata.octoVersion || item.metadata.infraVersion) && (
                                                <div className="version-info-compact mb-3">
                                                    <div className="d-flex justify-content-center gap-1 flex-wrap">
                                                        {item.metadata.octoVersion && (
                                                            <span 
                                                                className="badge bg-light text-dark border"
                                                                style={{ fontSize: '0.6rem', padding: '0.2rem 0.4rem' }}
                                                                title={`Octo: ${item.metadata.octoVersion}`}
                                                            >
                                                                <i className="bi bi-tag me-1"></i>
                                                                {item.metadata.octoVersion.length > 12 
                                                                    ? item.metadata.octoVersion.substring(0, 12) + '...'
                                                                    : item.metadata.octoVersion
                                                                }
                                                            </span>
                                                        )}
                                                        
                                                        {item.metadata.infraVersion && (
                                                            <span 
                                                                className="badge bg-light text-dark border"
                                                                style={{ fontSize: '0.6rem', padding: '0.2rem 0.4rem' }}
                                                                title={`Infra: ${item.metadata.infraVersion}`}
                                                            >
                                                                <i className="bi bi-gear me-1"></i>
                                                                {item.metadata.infraVersion}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Primary Action Buttons - Most Prominent */}
                                            <div className="d-flex gap-2 justify-content-center flex-wrap">
                                                {currentStep !== 'app' ? (
                                                    <motion.button 
                                                        className={`btn btn-${stepInfo.color} btn-sm fw-semibold`}
                                                        onClick={() => navigateToFolder(item.path)}
                                                        whileHover={{ scale: 1.05 }}
                                                        whileTap={{ scale: 0.95 }}
                                                    >
                                                        <i className="bi bi-arrow-right me-1"></i>
                                                        Select {currentStep === 'typology' ? 'Typology' : 'Cluster'}
                                                    </motion.button>
                                                ) : (
                                                    <motion.button 
                                                        className={`btn btn-${stepInfo.color} btn-sm fw-semibold`}
                                                        onClick={() => navigateToFolder(item.path)}
                                                        whileHover={{ scale: 1.05 }}
                                                        whileTap={{ scale: 0.95 }}
                                                    >
                                                        <i className="bi bi-rocket me-1"></i>
                                                        Deploy App
                                                    </motion.button>
                                                )}
                                                
                                                {/* Reservation Buttons for Internal Apps - Secondary but Clear */}
                                                {isInternalAppItem && (
                                                    <>
                                                        {!reservation?.reserved ? (
                                                            <motion.button 
                                                                className="btn btn-outline-primary btn-sm fw-semibold"
                                                                onClick={() => reserveApp(item.path)}
                                                                disabled={loadingReservations[item.path]}
                                                                whileHover={{ scale: 1.05 }}
                                                                whileTap={{ scale: 0.95 }}
                                                            >
                                                                {loadingReservations[item.path] ? (
                                                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                                                ) : (
                                                                    <i className="bi bi-lock me-1"></i>
                                                                )}
                                                                Reserve
                                                            </motion.button>
                                                        ) : (
                                                            <motion.button 
                                                                className="btn btn-outline-secondary btn-sm fw-semibold"
                                                                onClick={() => releaseApp(item.path)}
                                                                disabled={loadingReservations[item.path]}
                                                                whileHover={{ scale: 1.05 }}
                                                                whileTap={{ scale: 0.95 }}
                                                            >
                                                                {loadingReservations[item.path] ? (
                                                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                                                ) : (
                                                                    <i className="bi bi-unlock me-1"></i>
                                                                )}
                                                                Release
                                                            </motion.button>
                                                        )}
                                                    </>
                                                )}
                                                
                                                {/* Favorite Button - For all apps */}
                                                {getCurrentStep() === 'app' && (
                                                    <motion.button 
                                                        className={`btn btn-sm fw-semibold ${
                                                            favorite?.isFavorited 
                                                                ? 'btn-warning' 
                                                                : 'btn-outline-warning'
                                                        }`}
                                                        onClick={async () => {
                                                            if (favorite?.isFavorited) {
                                                                await removeFromFavorites(item.path);
                                                            } else {
                                                                const pathParts = item.path.split('/');
                                                                await addToFavorites(
                                                                    item.path, 
                                                                    itemName, 
                                                                    pathParts[0], 
                                                                    pathParts[1]
                                                                );
                                                            }
                                                        }}
                                                        disabled={loadingFavorites[item.path]}
                                                        whileHover={{ scale: 1.05 }}
                                                        whileTap={{ scale: 0.95 }}
                                                        title={favorite?.isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                                                    >
                                                        {loadingFavorites[item.path] ? (
                                                            <span className="spinner-border spinner-border-sm me-1"></span>
                                                        ) : (
                                                            <i className={`bi ${favorite?.isFavorited ? 'bi-star-fill' : 'bi-star'} me-1`}></i>
                                                        )}
                                                        {favorite?.isFavorited ? 'Favorited' : 'Favorite'}
                                                    </motion.button>
                                                )}

                                                {/* Mute Button - Tertiary Action */}
                                                {item.metadata?.cluster && (
                                                    <motion.button 
                                                        className="btn btn-outline-danger btn-sm fw-semibold"
                                                        onClick={async () => {
                                                            await muteClusterAlerts(item.metadata.cluster);
                                                        }}
                                                        whileHover={{ scale: 1.05 }}
                                                        whileTap={{ scale: 0.95 }}
                                                    >
                                                        <i className="bi bi-volume-mute-fill me-1"></i>
                                                        Mute
                                                    </motion.button>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Empty State */}
                    {filteredItems.length === 0 && (
                        <motion.div 
                            className="empty-state text-center py-5"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            <i className="bi bi-folder-x display-1 text-muted mb-3"></i>
                            <h5 className="text-muted">No {currentStep} found</h5>
                            <p className="text-muted">
                                {currentStep === 'typology' ? 'No typologies available' :
                                 currentStep === 'cluster' ? 'No clusters found for this typology' :
                                 'No apps found for this cluster'}
                            </p>
                        </motion.div>
                    )}
                </motion.div>
            )}
        </div>
    );
};

export default RepoStructure;
